import * as React from "react";
import { useFormContext } from "react-hook-form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { CheckCircle2, Clock, AlertCircle, ChevronDown, MoreVertical, Check, FileText } from "lucide-react";
import { ReceiptModal } from "@/components/receipts/ReceiptModal";
import { useClients } from "@/hooks/useClients";
import { Transaction } from "@/types";

interface TransactionStatusSelectorProps {
  disabled?: boolean;
}

/**
 * Builds a Transaction object from form values for receipt generation.
 * Maps form field names to Transaction interface properties.
 */
function buildTransactionFromForm(formValues: Record<string, unknown>, transactionId?: string): Transaction {
  const now = new Date().toISOString();

  return {
    id: transactionId || `temp-${Date.now()}`,
    user_id: formValues.user_id || "",
    type: formValues.type || "income",
    amount: formValues.amount || 0,
    description: formValues.description || "",
    category_id: formValues.category_id || 0,
    payment_account_id: formValues.payment_account_id || 0,
    cost_center_id: formValues.cost_center_id || 0,
    date: formValues.date || now,
    competence_date: formValues.competence_date || null,
    due_date: formValues.due_date || null,
    is_paid: formValues.is_paid ?? (formValues.status === "PAID"),
    status: formValues.status || "PENDING",
    notes: formValues.notes || "",
    created_at: formValues.created_at || now,
    updated_at: formValues.updated_at || now,
    parent_transaction_id: formValues.parent_transaction_id,
    total_installments: formValues.total_installments,
    current_installment: formValues.current_installment,
    payment_method: formValues.payment_method,
    payment_date: formValues.payment_date,
    is_scheduled: formValues.is_scheduled,
    client_id: formValues.client_id,
    supplier_id: formValues.supplier_id,
    category: formValues.category,
    categories: formValues.categories,
    is_recurring: formValues.is_recurring,
    recurrence_pattern: formValues.recurrence_pattern,
    recurrence_interval: formValues.recurrence_interval,
    recurrence_count: formValues.recurrence_count,
    recurrence_created_count: formValues.recurrence_created_count,
    recurrence_end_date: formValues.recurrence_end_date,
    recurrence_frequency: formValues.recurrence_frequency,
    next_recurrence_date: formValues.next_recurrence_date,
  };
}

/**
 * Transforms client data to the format expected by ReceiptModal.
 */
function getClientDataForReceipt(client: { name: string; email?: string; phone?: string } | null | undefined) {
  if (!client) return undefined;
  return {
    name: client.name,
    email: client.email,
    phone: client.phone,
  };
}

export function TransactionStatusSelector({ disabled = false }: TransactionStatusSelectorProps) {
  const form = useFormContext();
  const status = form.watch("status");
  const isPaid = form.watch("isPaid");
  const clientId = form.watch("client_id");
  const transactionId = form.watch("id");

  // State for receipt modal
  const [isReceiptModalOpen, setIsReceiptModalOpen] = React.useState(false);

  // Fetch clients to get client data for receipt
  const { clients } = useClients();

  // Find the client associated with this transaction
  const clientData = React.useMemo(() => {
    if (!clientId) return undefined;
    const client = clients.find(c => c.id === clientId);
    if (!client) return undefined;
    return getClientDataForReceipt({
      name: client.name,
      email: client.email,
      phone: client.phone,
    });
  }, [clientId, clients]);

  // Build transaction object for receipt
  const transactionForReceipt = React.useMemo(() => {
    const formValues = form.getValues();
    return buildTransactionFromForm(formValues, transactionId);
  }, [form, transactionId]);

  // Handle receipt emission
  const handleEmitReceipt = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsReceiptModalOpen(true);
  }, []);

  // Sync status with isPaid for backward compatibility
  const handleStatusChange = React.useCallback((newStatus: string) => {
    // Update status field
    form.setValue("status", newStatus);

    // Sync isPaid field when status changes
    if (newStatus === "PAID") {
      form.setValue("isPaid", true);
    } else if (isPaid && newStatus !== "PAID") {
      form.setValue("isPaid", false);
    }
  }, [form, isPaid]);

  // Sync status with isPaid when isPaid changes elsewhere (but only if status is not already set correctly)
  React.useEffect(() => {
    if (isPaid && status !== "PAID") {
      // Only auto-set to PAID if current status is PENDING or empty (preserve OVERDUE, CANCELLED, etc.)
      if (status === "PENDING" || !status) {
        form.setValue("status", "PAID");
      }
    }
    // Removed the automatic fallback to PENDING to preserve valid statuses
  }, [isPaid, status, form]);

  // Fallback status configuration in case useDesignTokens hook is not available
  const currentStatusConfig = {
    all: cn(
      "px-2 py-1",
      status === "PAID" ? "bg-finance-green/10 text-finance-green border-finance-green/30" :
      status === "PENDING" ? "bg-finance-amber/10 text-finance-amber border-finance-amber/30" :
      status === "OVERDUE" ? "bg-finance-red/10 text-finance-red border-finance-red/30" :
      status === "CANCELLED" ? "bg-muted/50 text-muted-foreground border-muted-foreground/30" :
      "bg-muted/50 text-muted-foreground border-muted-foreground/30"
    )
  };

  const getStatusInfo = (currentStatus: string) => {
    return {
      label: currentStatus === "PAID" ? "Pago" :
             currentStatus === "PENDING" ? "Pendente" :
             currentStatus === "OVERDUE" ? "Vencido" :
             currentStatus === "CANCELLED" ? "Cancelado" : "Pendente",
      description: currentStatus === "PAID" ? "Pagamento ja realizado" :
                    currentStatus === "PENDING" ? "Aguardando pagamento" :
                    currentStatus === "OVERDUE" ? "Pagamento em atraso" :
                    currentStatus === "CANCELLED" ? "Pagamento cancelado" : "Aguardando pagamento",
      IconComponent: currentStatus === "PAID" ? CheckCircle2 :
                   currentStatus === "PENDING" ? Clock :
                   currentStatus === "OVERDUE" ? AlertCircle : Clock,
    };
  };

  return (
    <>
      <FormField
        control={form.control}
        name="status"
        render={({ field }) => (
          <FormItem className="space-y-2">
            <FormLabel className="text-sm font-semibold flex items-center gap-2">
              <span>Status do Pagamento</span>
              <Badge
                variant="secondary"
                className={cn(
                  "text-[10px] font-medium px-1.5 py-0.5 h-5",
                  currentStatusConfig.all
                )}
              >
                {getStatusInfo(status).label}
              </Badge>
            </FormLabel>

            <Select value={field.value} onValueChange={handleStatusChange} disabled={disabled}>
              <FormControl>
                <SelectTrigger className="h-10 text-sm rounded-lg border-2 bg-background hover:border-primary/50 transition-all duration-200">
                  <SelectValue placeholder="Selecione o status" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="PAID" className="hover:bg-accent/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-finance-green" />
                    <span className="text-sm">Pago</span>
                  </div>
                </SelectItem>
                <SelectItem value="PENDING" className="hover:bg-accent/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-finance-amber" />
                    <span className="text-sm">Pendente</span>
                  </div>
                </SelectItem>
                <SelectItem value="OVERDUE" className="hover:bg-accent/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-finance-red" />
                    <span className="text-sm">Vencido</span>
                  </div>
                </SelectItem>
                <SelectItem value="CANCELLED" className="hover:bg-accent/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Cancelado</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Quick Action Buttons */}
            <div className="flex gap-2 mt-2">
              {/* Outras Acoes Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md"
                    disabled={disabled}
                  >
                    <MoreVertical className="h-4 w-4 mr-2" />
                    Outras acoes
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-48">
                  <DropdownMenuItem className="cursor-pointer">
                    <Check className="h-4 w-4 mr-2 text-finance-green" />
                    Marcar como pago
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsReceiptModalOpen(true);
                    }}
                  >
                    <FileText className="h-4 w-4 mr-2 text-primary" />
                    Emitir recibo
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer">
                    <AlertCircle className="h-4 w-4 mr-2 text-finance-red" />
                    Cancelar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Conditional Action Button */}
              {status === "PAID" ? (
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md"
                  disabled={disabled}
                  onClick={handleEmitReceipt}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Emitir Recibo
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md"
                  disabled={disabled}
                  onClick={(e) => {
                    e.preventDefault();
                    handleStatusChange("PAID");
                  }}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Marcar como pago
                </Button>
              )}
            </div>

            <FormMessage />
          </FormItem>
        )}
      />

      {/* Receipt Modal */}
      <ReceiptModal
        open={isReceiptModalOpen}
        onOpenChange={setIsReceiptModalOpen}
        transaction={transactionForReceipt}
        client={clientData}
      />
    </>
  );
}
